'use client';

import { Button, Spinner } from '@common/components';
import { SvgChevronright } from '@common/icon';
import { cn } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { UnauthInfiniteQueryOptions } from '@/api';
import { ReviewDetailSessionPortal } from '@/app/donation/_components/ReviewDetailSessionPortal';
import { Image } from '@/components/client';
import { SessionPortal } from '@/components/organism/SessionPortal/SessionPortal';
import { SELECTOR } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { openResponsiveDialog } from '@/stores/DrawerStore';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';

import { PhotoReviewsModalContent } from '../gifts/[productNumber]/_components/PhotoReviewsModalContent';

const INITIAL_PAGE = 1;
const INITIAL_SIZE = 10;

interface PhotoReviewSwiperProps {
  photoReviewList: {
    fileUrl: string;
    reviewId: number;
  }[];
  photoReviewCount: number;
  productNumber?: string;
  institutionId?: number;
  className?: string;
}

export const PhotoReviewSwiper = ({
  photoReviewList,
  photoReviewCount,
  productNumber,
  institutionId,
  className,
}: PhotoReviewSwiperProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  const detailReviewIdParam = searchParams.get('detailReviewId');
  const detailReviewId = detailReviewIdParam
    ? Number(detailReviewIdParam)
    : undefined;

  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    dragFree: true,
    containScroll: 'trimSnaps',
  });

  const handleDetailReviewId = (reviewId?: number) => {
    openResponsiveDialog({
      component: PhotoReviewsModalContent,
      modalHeader: '포토후기',
      className: {
        modal: 'max-w-[900px]',
      },
      props: {
        productNumber,
        institutionId,
        photoReviewList,
        photoReviewCount,
        initialReviewId: reviewId,
      },
      drawerNoPadding: true,
    });
  };

  //* 더보기 포토후기 목록
  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    ...filledInfiniteQueryOptions(
      UnauthInfiniteQueryOptions['get:/wello/v2/allows/home/gift-review/photo'](
        {
          query: {
            productNumber,
            institutionId,
            page: INITIAL_PAGE,
            size: INITIAL_SIZE,
          },
        },
      ),
    ),
    enabled: !!productNumber || !!institutionId,
  });

  const infinitePhotoReviewList =
    data?.pages.flatMap((page) => page.context?.contents ?? []) ?? [];

  const { ref, inView } = useInView();

  useEffect(() => {
    if (!isFetching && inView) fetchNextPage();
  }, [inView, isFetching, fetchNextPage, hasNextPage]);

  const isEmpty = photoReviewCount < 1;

  if (isEmpty) return null;

  return (
    <section className={cn('flex flex-col pb-24', className)}>
      <div className="flex items-center justify-between py-12">
        <h2 className="font-700 text-Gray-900 typo-body2">
          포토후기 {photoReviewCount.toLocaleString()}
        </h2>

        <Button
          theme="sub-text"
          className="flex size-fit items-center gap-4 px-4 py-10 font-400 text-Gray-900 typo-body4"
          disabled={isEmpty}
          onClick={() => {
            if (isDesktopViewport) {
              openResponsiveDialog({
                component: PhotoReviewsModalContent,
                modalHeader: '포토후기 전체보기',
                className: {
                  modal: 'max-w-[900px]',
                },
                props: {
                  institutionId,
                  productNumber,
                  photoReviewList,
                  photoReviewCount,
                },
              });
            } else {
              setIsOpen(true);
            }
          }}
        >
          더보기
          <SvgChevronright className="size-12" />
        </Button>
      </div>
      <div className="w-full overflow-hidden" ref={emblaRef}>
        <div className="flex gap-8">
          {photoReviewList.map(({ fileUrl, reviewId }) => (
            <div key={reviewId} className="w-fit flex-[0_0_auto]">
              <button
                type="button"
                className="relative size-104 overflow-hidden rounded-small border border-Gray-black/5"
                onClick={() => handleDetailReviewId(reviewId)}
              >
                <Image
                  src={fileUrl}
                  alt="포토후기 이미지"
                  width={850}
                  height={850}
                  className="size-full object-cover object-center"
                />
              </button>
            </div>
          ))}
        </div>
      </div>
      {!isDesktopViewport && isOpen ? (
        <SessionPortal
          renderTo={SELECTOR.MAIN_WRAPPER}
          wrapperClassName="z-10"
          onClose={() => setIsOpen(false)}
        >
          <div className="flex flex-col">
            <ul className="grid grid-cols-3 gap-12 px-20 pt-12">
              {infinitePhotoReviewList.map(({ fileUrl: file_url, reviewId: review_id }) => (
                <li key={review_id}>
                  <button
                    type="button"
                    className="relative aspect-square overflow-hidden rounded-small border border-Gray-black/5"
                    onClick={() => handleDetailReviewId(review_id)}
                  >
                    <Image
                      src={file_url}
                      alt="포토후기 이미지"
                      width={300}
                      height={300}
                      className="size-full object-cover object-center"
                    />
                  </button>
                </li>
              ))}
            </ul>
            {hasNextPage ? (
              <div className="my-30 w-full flex-center" ref={ref}>
                <Spinner />
              </div>
            ) : null}
          </div>
        </SessionPortal>
      ) : (
        <></>
      )}
      <ReviewDetailSessionPortal
        open={!isDesktopViewport && !!detailReviewId}
        onClose={() => handleDetailReviewId(undefined)}
        reviewId={detailReviewId}
      />
    </section>
  );
};
